import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Info, TrendingUp, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAccount, useNetwork, useWalletClient } from 'wagmi';
import { 
  getDexName, 
  getDexVersion, 
  getNativeTokenSymbol, 
  isV3Supported,
  isV2Supported,
  isDexSupported,
  getDexContracts,
  V3_FEE_TIERS
} from '@/config/dex-v3-config';
import { UserTokenSelector } from '@/components/token/UserTokenSelector';
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';
import { toast } from 'sonner';
import { ethers } from 'ethers';
import { getTxOptions } from '@/utils/gas';
import { isAddress } from 'viem';
import { usePoolExists } from '@/hooks/usePoolExists';
import { useAddLiquidity } from '@/hooks/useAddLiquidity';
import { useV2PoolExists } from '@/hooks/useV2PoolExists';
import { useAddV2Liquidity } from '@/hooks/useAddV2Liquidity';

export function AddLiquidity() {
  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const { data: walletClient } = useWalletClient();
  const { settings: feeSettings, loading: feeLoading } = useFirebaseFees();
  const [tokenAddress, setTokenAddress] = useState('');
  const [tokenAmount, setTokenAmount] = useState('');
  const [nativeAmount, setNativeAmount] = useState('');
  const [dexName, setDexName] = useState<string | null>(null);
  const [dexVersion, setDexVersion] = useState<'V2' | 'V3' | null>(null);
  const [nativeSymbol, setNativeSymbol] = useState('ETH');
  const [isV3, setIsV3] = useState(false);
  const [isV2, setIsV2] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [wethAddress, setWethAddress] = useState<string>('');

  useEffect(() => {
    if (chain?.id) {
      const chainId = chain.id;
      setDexName(getDexName(chainId));
      setDexVersion(getDexVersion(chainId));
      setNativeSymbol(getNativeTokenSymbol(chainId));
      setIsV3(isV3Supported(chainId));
      setIsV2(isV2Supported(chainId));
      setIsSupported(isDexSupported(chainId));

      const contracts = getDexContracts(chainId);
      if (contracts) {
        setWethAddress(contracts.weth);
      }
    }
  }, [chain?.id]);

  // Helper function to get network fee
  const getNetworkFee = (feeType: 'addLiquidityFee') => {
    if (!feeSettings?.networkFees || !chain?.id) return '0';
    
    // Map testnet to mainnet for fee lookup
    const mainnetChainId = {
      97: 56,        // BSC Testnet -> BSC Mainnet
      5611: 204,     // opBNB Testnet -> opBNB Mainnet
      11155111: 1,   // Sepolia -> Ethereum Mainnet
      43113: 43114,  // Fuji Testnet -> Avalanche Mainnet
      11124: 2741,   // Abstract Sepolia -> Abstract Mainnet
      421614: 42161, // Arbitrum Sepolia -> Arbitrum One
      84532: 8453,   // Base Sepolia -> Base Mainnet
      11155420: 10,  // Optimism Sepolia -> Optimism Mainnet
      80002: 137,    // Polygon Amoy -> Polygon Mainnet
      1328: 1329     // Sei Testnet -> Sei Mainnet
    }[chain.id] || chain.id;

    // ETH-based networks that should use Ethereum fees if not found
    const ethBasedNetworks = [2741, 11124, 42161, 421614, 8453, 84532, 10, 11155420];
    
    let networkFees = feeSettings.networkFees[mainnetChainId];
    
    // If no fees found and it's an ETH-based network, use Ethereum fees
    if (!networkFees && ethBasedNetworks.includes(mainnetChainId)) {
      networkFees = feeSettings.networkFees[1]; // Ethereum mainnet
    }
    
    if (!networkFees) return '0';
    
    return networkFees[feeType] || '0';
  };

  const isValidToken = tokenAddress && isAddress(tokenAddress);
  const hasWeth = Boolean(wethAddress);

  // V3 liquidity params & hooks (when supported)
  const v3LiquidityParams = isV3 && isValidToken && hasWeth && tokenAmount && nativeAmount && address
    ? {
        token0: tokenAddress,
        token1: wethAddress,
        feeTier: V3_FEE_TIERS.MEDIUM,
        amount0: tokenAmount,
        amount1: nativeAmount,
        recipient: address,
      }
    : null;

  const { exists: v3PoolExists } = usePoolExists(
    isV3 && isValidToken && hasWeth ? tokenAddress : undefined,
    hasWeth ? wethAddress : undefined,
    V3_FEE_TIERS.MEDIUM
  );

  const { addLiquidity: addV3Liquidity, isAdding: isAddingV3, canAdd: canAddV3 } = useAddLiquidity(v3LiquidityParams);

  // V2 liquidity params & hooks (for V2-only networks)
  const { exists: v2PoolExists } = useV2PoolExists(
    !isV3 && isV2 && isValidToken && hasWeth ? tokenAddress : undefined,
    hasWeth ? wethAddress : undefined
  );

  const v2LiquidityParams = !isV3 && isV2 && isValidToken && tokenAmount && nativeAmount && address
    ? {
        token: tokenAddress,
        tokenAmount,
        nativeAmount,
        recipient: address,
      }
    : null;

  const { addLiquidity: addV2Liquidity, isAdding: isAddingV2, canAdd: canAddV2 } = useAddV2Liquidity(v2LiquidityParams);

  const poolExists = isV3 ? v3PoolExists : v2PoolExists;
  const isAdding = isV3 ? isAddingV3 : isAddingV2;
  const canAdd = isV3 ? canAddV3 : canAddV2;

  const handleAddLiquidity = async () => {
    try {
      if (!isConnected) {
        toast.error('Wallet not connected');
        return;
      }

      if (!chain?.id || !isSupported || (!isV3 && !isV2)) {
        toast.error('This network does not have a supported DEX for adding liquidity.');
        return;
      }

      if (!tokenAddress || !isAddress(tokenAddress)) {
        toast.error('Please enter a valid token address.');
        return;
      }

      if (!tokenAmount || !nativeAmount) {
        toast.error('Please enter both token and native amounts.');
        return;
      }

      if (!walletClient) {
        toast.error('Wallet not connected');
        return;
      }

      // Check fee settings
      if (!feeSettings?.networkFees || feeLoading) {
        toast.error('Fee settings not loaded. Please try again.');
        return;
      }

      const feePercentage = parseFloat(getNetworkFee('addLiquidityFee'));
      if (isNaN(feePercentage) || feePercentage < 0) {
        toast.error('Invalid fee configuration');
        return;
      }

      const native = parseFloat(nativeAmount);
      if (isNaN(native) || native <= 0) {
        toast.error('Invalid native token amount');
        return;
      }

      const feeAmount = (native * feePercentage) / 100;
      const feeCollectorAddress = feeSettings.collectors[0].address;

      if (feeAmount > 0) {
        toast.info(`Collecting ${feePercentage}% platform fee (${feeAmount.toFixed(6)} ${nativeSymbol})...`);

        const provider = new ethers.providers.Web3Provider(walletClient.transport, 'any');
        const signer = provider.getSigner();
        const platformFeeAmount = ethers.utils.parseEther(feeAmount.toString());

        const feeTx = await signer.sendTransaction({
          to: feeCollectorAddress,
          value: platformFeeAmount,
          ...getTxOptions(50000),
        });

        toast.info('Processing platform fee payment...');
        await feeTx.wait();
        toast.success('Platform fee paid successfully!');
      }

      // Ensure pool exists before adding
      if (!poolExists) {
        toast.error('No existing pool found for this token on the selected DEX. Please create a pool first using the Launch Pool tab.');
        return;
      }

      if (isV3) {
        if (!canAddV3) {
          toast.error('Cannot add liquidity. Check your token approvals and amounts.');
          return;
        }
        addV3Liquidity();
      } else if (isV2) {
        if (!canAddV2) {
          toast.error('Cannot add liquidity. Check your token approvals and amounts.');
          return;
        }
        addV2Liquidity();
      }
    } catch (error: any) {
      console.error('Error in handleAddLiquidity:', error);
      toast.error(error?.message || 'Failed to add liquidity. Please try again.');
    }
  };

  const canSubmit = Boolean(
    isConnected &&
    isSupported &&
    tokenAddress &&
    tokenAmount &&
    nativeAmount &&
    !isAdding
  );

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Add more liquidity to an existing pool. You'll earn a portion of trading fees proportional to your share of the pool.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-green-500" />
            Add Liquidity
          </CardTitle>
          <CardDescription>
            Increase liquidity in an existing pool
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* User Token Selector */}
          <UserTokenSelector
            onSelectToken={(address) => setTokenAddress(address)}
            selectedToken={tokenAddress}
            label="Quick Select Your Token"
          />

          {/* Token Address */}
          <div className="space-y-2">
            <Label htmlFor="token-address">Token Address</Label>
            <Input
              id="token-address"
              placeholder="0x... or select from your tokens above"
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The token you want to add liquidity for
            </p>
          </div>

          {/* Token Amount */}
          <div className="space-y-2">
            <Label htmlFor="add-token-amount">Token Amount</Label>
            <Input
              id="add-token-amount"
              type="number"
              placeholder="0.0"
              value={tokenAmount}
              onChange={(e) => setTokenAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Amount of your token to add
            </p>
          </div>

          {/* Native Token Amount */}
          <div className="space-y-2">
            <Label htmlFor="add-native-amount">{nativeSymbol} Amount</Label>
            <Input
              id="add-native-amount"
              type="number"
              placeholder="0.0"
              value={nativeAmount}
              onChange={(e) => setNativeAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Amount of {nativeSymbol} to add (must match pool ratio)
            </p>
          </div>

          {/* Pool Share Info */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your Pool Share</span>
                  <span className="font-medium">--%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Pool Tokens</span>
                  <span className="font-medium">-- LP</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Est. APR
                  </span>
                  <span className="font-medium text-green-500">--%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button 
              className="flex-1" 
              size="lg" 
              disabled={!canSubmit}
              onClick={handleAddLiquidity}
            >
              {isAdding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding Liquidity...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Liquidity
                </>
              )}
            </Button>
            <Button variant="outline" size="lg" onClick={() => {
              setTokenAddress('');
              setTokenAmount('');
              setNativeAmount('');
            }}>
              Clear
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Note: The pool for this token must already exist on the selected DEX. If it does not, create it first using the Launch Pool tab.
          </p>

          {/* Platform Fee Display */}
          {!feeLoading && feeSettings?.networkFees && chain?.id && (
            <Alert className="border-primary/50 bg-primary/10 mt-4">
              <Info className="h-4 w-4 text-primary" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span>
                    <strong>Platform Fee:</strong> {getNetworkFee('addLiquidityFee')}% of {nativeSymbol} amount
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (Collected before adding liquidity)
                  </span>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
